import { useMemo } from "react";
import LazyApexChart from "../../../client/components/LazyApexChart";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtShort(v: number): string {
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(1);
}

export default function NetDailyPnlWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "date", label: t.analytics.dateLabel, align: "left" as const },
    { key: "pnl", label: t.analytics.netDailyPnlLabel, align: "right" as const },
  ];
  const tableRows = (analyticsData?.netDailyPnl ?? []).map((d) => ({
    date: new Date(d.date).toLocaleDateString(undefined),
    pnl: (
      <span className={d.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {d.pnl >= 0 ? "+" : ""}
        {d.pnl.toFixed(2)}
      </span>
    ),
  }));

  const chartOptions = useMemo(() => {
    if (!analyticsData || analyticsData.netDailyPnl.length === 0) return null;
    const { netDailyPnl } = analyticsData;
    const c = chartColors();
    return {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
      },
      plotOptions: {
        bar: { borderRadius: 3, columnWidth: "60%", distributed: true },
      },
      xaxis: {
        categories: netDailyPnl.map((d) => d.date),
        labels: {
          style: { colors: c.axis, fontSize: "10px" },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: number) => fmtShort(v) + "$",
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          formatter: (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`,
        },
      },
      colors: netDailyPnl.map((d) => pnlColor(d.pnl)),
      legend: { show: false },
      dataLabels: { enabled: false },
    };
  }, [analyticsData, isDark]);

  if (!analyticsData || analyticsData.netDailyPnl.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.dashboard.widgets.netDailyPnl}
          isDark={isDark}
          compact
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.netDailyPnl}
        isDark={isDark}
        compact
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 px-5 pb-5 pt-1">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : chartOptions ? (
          <LazyApexChart
            key={`net-daily-pnl-widget-${themeId}`}
            options={chartOptions}
            series={[
              {
                name: t.analytics.netDailyPnlLabel,
                data: analyticsData.netDailyPnl.map(
                  (d) => Math.round(d.pnl * 100) / 100,
                ),
              },
            ]}
            type="bar"
            height={240}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
